/**
 * Route Constants
 *
 * Centralized route definitions and patterns for navigation.
 */

/**
 * Application routes
 */
export const ROUTES = {
  /** All projects list */
  ALL_PROJECTS: '/all-projects',

  /** Single project view (with dynamic projectId) */
  PROJECT: '/project/:projectId',

  /** Project with tab parameter */
  PROJECT_TAB: '/project/:projectId',

  /** Audiences page (project sub-route) */
  AUDIENCES: '/project/:id/audiences',

  /** Settings page (admin) */
  SETTINGS: '/settings',

  /** Guides page (admin) */
  GUIDES: '/guides',

  /** Users management page (admin) */
  USERS: '/users',

  /** Strategies library (admin) */
  STRATEGIES_LIBRARY: '/strategies-library',

  /** Themes management (admin) */
  THEMES: '/themes',

  /** Themes V2 management (admin) */
  THEMES_V2: '/themes-v2',

  /** Usage analytics (admin) */
  USAGE: '/usage',

  /** Cron-tasks admin dashboard (Sprint 20) */
  CRON_TASKS_ADMIN: '/admin/cron-tasks',

  /** Provider credentials admin */
  PROVIDER_CREDENTIALS_ADMIN: '/admin/provider-credentials',

  /** Segmently billing catalog admin */
  BILLING_CATALOG_ADMIN: '/admin/billing-catalog',

  /** Content-plan system platform catalogue */
  SYSTEM_PLATFORMS_ADMIN: '/admin/system-platforms',

  /** Ad creative advertising platform catalogue */
  AD_PLATFORMS_ADMIN: '/admin/ad-platforms',

  /** Onboardings list */
  ONBOARDINGS: '/onboardings',

  /** Onboardings V2 list (Schema V2 migration) */
  ONBOARDINGS_V2: '/onboardings-v2',

  /** Placements list (project sub-route) */
  PLACEMENTS: '/project/:projectId/placements',

  /** AB Tests list (project sub-route) */
  ABTESTS: '/project/:projectId/ab-tests',

  /** Web A/B Tests list (project sub-route) */
  WEB_ABTESTS: '/project/:projectId/web-ab-tests',

  /** Stripe Integration (project sub-route) */
  STRIPE_INTEGRATION: '/project/:projectId/stripe/integration',

  /** Paywall Products (project sub-route) */
  STRIPE_PRODUCTS: '/project/:projectId/stripe/products',

  /** Analytics Settings (project sub-route) */
  ANALYTICS: '/project/:projectId/analytics',

  /** Project Segmently billing page */
  BILLING: '/project/:projectId/billing',

  /** Unified Integrations page (project sub-route) */
  INTEGRATIONS: '/project/:projectId/integrations',

  /** Content Plan top-level admin-only route (project-scoped) */
  CONTENT_PLAN: '/project/:projectId/content-plan',

  /** Strategy canvas editor (full-screen, no sidebar) */
  STRATEGY_CANVAS: '/project/:projectId/strategy/:strategyId',

  /** Block screens canvas editor (full-screen, no sidebar) */
  BLOCK_CANVAS: '/project/:projectId/strategy/:strategyId/block/:blockId',

  /** Login page */
  LOGIN: '/login',
} as const;

/**
 * Project tab values
 */
export const PROJECT_TABS = {
  PRODUCT: 'product',
  AUDIENCES: 'audiences',
  ACTORS: 'actors',
  SOCIAL_PROOFS: 'socialproofs',
  STRATEGIES: 'strategies',
} as const;

export type ProjectTab = (typeof PROJECT_TABS)[keyof typeof PROJECT_TABS];

/**
 * Route patterns for matching
 */
export const ROUTE_PATTERNS = {
  /** Matches any project route */
  PROJECT: /^\/project\/([^/]+)/,

  /** Matches project ID from URL */
  PROJECT_ID: /\/project\/([^/?]+)/,

  /** Admin routes pattern */
  ADMIN: /^\/(settings|guides|users|strategies-library|themes(-v2)?|usage|admin\/)/,
} as const;

/**
 * Extract project ID from pathname
 */
export function extractProjectIdFromPathname(pathname: string): string | null {
  const match = pathname.match(ROUTE_PATTERNS.PROJECT_ID);
  return match ? match[1] : null;
}

/**
 * Check if pathname is a project page
 */
export function isProjectPage(pathname: string): boolean {
  return ROUTE_PATTERNS.PROJECT.test(pathname);
}

/**
 * Check if pathname is an admin page
 */
export function isAdminPage(pathname: string): boolean {
  return ROUTE_PATTERNS.ADMIN.test(pathname);
}

/**
 * Build project URL
 */
export function buildProjectUrl(projectId: string, tab?: ProjectTab): string {
  const base = `/project/${projectId}`;
  return tab ? `${base}?tab=${tab}` : base;
}

/**
 * Build content-plan URL (top-level admin-only route)
 */
export function buildContentPlanUrl(projectId: string, cpTab?: string): string {
  const base = `/project/${projectId}/content-plan`;
  return cpTab ? `${base}?cpTab=${cpTab}` : base;
}

/**
 * Build strategy canvas URL (full-screen editor)
 */
export function buildStrategyCanvasUrl(projectId: string, strategyId: string): string {
  return `/project/${projectId}/strategy/${strategyId}`;
}

/**
 * Query parameter used by the strategy canvas to open a block's simplified
 * screen canvas as a URL-addressable modal.
 */
export const STRATEGY_BLOCK_MODAL_PARAM = 'blockId';
export const STRATEGY_SCREEN_MODAL_PARAM = 'screenId';

/**
 * Build strategy canvas URL with a block screen modal opened.
 */
export function buildStrategyBlockModalUrl(projectId: string, strategyId: string, blockId: string): string {
  const params = new URLSearchParams({ [STRATEGY_BLOCK_MODAL_PARAM]: blockId });
  return `${buildStrategyCanvasUrl(projectId, strategyId)}?${params.toString()}`;
}

/**
 * Build strategy canvas URL with a block screen modal opened and a specific
 * screen editor selected inside that block.
 */
export function buildStrategyBlockScreenModalUrl(
  projectId: string,
  strategyId: string,
  blockId: string,
  screenId: string,
): string {
  const params = new URLSearchParams({
    [STRATEGY_BLOCK_MODAL_PARAM]: blockId,
    [STRATEGY_SCREEN_MODAL_PARAM]: screenId,
  });
  return `${buildStrategyCanvasUrl(projectId, strategyId)}?${params.toString()}`;
}

/**
 * Build block screens canvas URL (full-screen editor for block screens)
 */
export function buildBlockCanvasUrl(projectId: string, strategyId: string, blockId: string): string {
  return `/project/${projectId}/strategy/${strategyId}/block/${blockId}`;
}

/**
 * Build block screens canvas URL with a specific screen editor selected.
 */
export function buildBlockScreenCanvasUrl(
  projectId: string,
  strategyId: string,
  blockId: string,
  screenId: string,
): string {
  const params = new URLSearchParams({ [STRATEGY_SCREEN_MODAL_PARAM]: screenId });
  return `${buildBlockCanvasUrl(projectId, strategyId, blockId)}?${params.toString()}`;
}

/**
 * Get tab from URL search params
 */
export function getTabFromSearchParams(search: string): ProjectTab | null {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  return tab && Object.values(PROJECT_TABS).includes(tab as ProjectTab)
    ? (tab as ProjectTab)
    : null;
}
